import {
  useContext,
  computed,
  useRouter,
  getCurrentInstance,
} from "@nuxtjs/composition-api";
import { useLogout, isAuthGuardedRoute } from "~/components/_refactored/auth";
import useFeatures from "~/composables/useFeatures";
import { useFavourites } from "~/composables/favourite/useFavourites";
import { useUserStore } from "~/stores/user";

/**
 * Merges groups of menu items with dividers between them.
 * Filters out empty groups and ensures no consecutive dividers appear.
 */
function mergeGroupsWithDividers(groups) {
  const nonEmptyGroups = groups.filter((group) => group.length > 0);

  const items = [];
  nonEmptyGroups.forEach((group, index) => {
    items.push(...group);
    if (index < nonEmptyGroups.length - 1) {
      items.push({ isDivider: true });
    }
  });

  return items;
}

export function useUserMenu() {
  const userStore = useUserStore();
  const { i18n, localePath, $helpers, $branding, $flags } = useContext();
  const router = useRouter();
  const instance = getCurrentInstance();
  const logout = useLogout();
  const { FEATURES } = useFeatures();
  const { favouriteCount } = useFavourites();

  const userMenu = computed(() => {
    if (userStore.meLoading) {
      return [
        {
          id: "login-spinner",
          text: i18n.t("navbar.loggingIn"),
          icon: "spinner",
        },
      ];
    }

    if (!userStore.me) {
      const guestItems = [];

      if ($branding.hasFeature(FEATURES.REGISTRATION)) {
        guestItems.push({
          id: "register-professional",
          click: async () => {
            await router.push(localePath({ name: "registrationPage" }));
          },
          text: $branding.isPriceOffer
            ? i18n.t("navbar.register")
            : i18n.t("navbar.registerAsProfessional"),
          icon: "user-plus",
        });
      }

      guestItems.push({
        id: "login",
        click: async () => {
          await router.push(localePath({ name: "login" }));
        },
        text: i18n.t("navbar.login"),
        icon: "sign-in-alt",
      });

      return guestItems;
    }

    // Group: Admin items
    const adminGroup = [];
    if (userStore.me.isAdmin) {
      adminGroup.push({
        id: "conductor",
        to: localePath({ name: "conductorIndexPage" }),
        text: i18n.t("navbar.user.conductor"),
        icon: "magic",
      });

      if ($branding.hasFeature(FEATURES.PROFESSIONALS) && userStore.me.isMate) {
        adminGroup.push({
          id: "profile",
          to: localePath({
            name: "professionalPage",
            params: { professionalSeoName: userStore.me.seoName },
          }),
          text: i18n.t("navbar.user.profile"),
          icon: "user",
        });
      }

      adminGroup.push({
        id: "back-to-index",
        to: localePath({ name: "indexPage" }),
        text: i18n.t("navbar.user.backToMainSite"),
        icon: "angle-double-left",
      });
    }

    // Group: Job market items
    const jobMarketGroup = [];
    if (userStore.me.isMate && $branding.hasFeature(FEATURES.JOB_MARKET)) {
      if (userStore.me.professions?.length || userStore.me.zones?.length) {
        jobMarketGroup.push({
          id: "buyTenders",
          to: localePath({ name: "buyTendersPage" }),
          text: i18n.t("navbar.user.buyTenders"),
          icon: "gas-pump",
        });
      }

      jobMarketGroup.push({
        id: "boughtTenders",
        to: localePath({ name: "boughtTendersPage" }),
        text: i18n.t("navbar.user.boughtTenders"),
        icon: "tasks",
      });
    }

    if ($flags.isFeatureEnabled("designers_enabled")) {
      jobMarketGroup.push({
        id: "designers",
        to: localePath({ name: "designersHubPage" }),
        text: i18n.t("navbar.designers.label"),
        icon: "pencil-ruler",
      });
    }

    if (
      $branding.hasFeature(FEATURES.PRICE_OFFER) &&
      (userStore.me.isMate || userStore.me.isPriceOfferProfessional)
    ) {
      jobMarketGroup.push({
        id: "priceOffers",
        to: localePath({ name: "myPriceOffersIndexPage" }),
        text: i18n.t("navbar.user.priceOffers"),
        icon: "file-alt",
        iconPack: "far",
      });

      jobMarketGroup.push({
        id: "productCatalog",
        to: localePath({ name: "productCatalogPage" }),
        text: i18n.t("navbar.user.productCatalog"),
        icon: "bookmark",
      });
    }

    // Group: Settings
    const settingsGroup = [];

    if (userStore.me.isMate) {
      settingsGroup.push({
        id: "subscription",
        to: localePath({ name: "subscriptionPage" }),
        text: i18n.t("navbar.user.subscription"),
        icon: "rocket",
      });

      settingsGroup.push({
        id: "addPrices",
        to: localePath({ name: "addPricesPage" }),
        text: i18n.t("navbar.user.addPrices"),
        icon: "dollar-sign",
      });
    }

    settingsGroup.push({
      id: "settings",
      to: localePath({ name: "settingsPage" }),
      text: i18n.t("navbar.user.settings"),
      icon: "cog",
    });

    // Group: CRM access — JóSzaki-brand admins only (for now). Opens the L2W CRM at crm.joszaki.hu via the
    // /joszaki-login SSO bridge, which mints an L2W session from the existing JóSzaki session (no separate
    // login / credentials). Gated to $branding.isJoszaki so it never shows on the other brands
    // (Berufexperten / Superprofik / price-offer), which have no such CRM. Non-admin pros excluded for now.
    const crmGroup = [];
    if (userStore.me.isAdmin && $branding.isJoszaki) {
      crmGroup.push({
        id: "goToCrm",
        to: "https://crm.joszaki.hu/joszaki-login",
        text: i18n.t("navbar.user.goToCrm"),
        icon: "external-link-alt",
        target: "_blank",
      });
    }

    // Group: Customer items
    const customerGroup = [];
    if (
      userStore.me.isCustomer &&
      $branding.hasFeature(FEATURES.REGISTRATION)
    ) {
      customerGroup.push({
        id: "registerAsProfessional",
        to: localePath({ name: "registrationPage" }),
        text: i18n.t("navbar.user.registerAsProfessional"),
        icon: "crown",
      });
    }

    if ($branding.hasFeature(FEATURES.FAVOURITES)) {
      customerGroup.push({
        id: "favourites",
        to: localePath({ name: "favourites" }),
        text: i18n.t("navbar.user.favorites", { count: favouriteCount.value }),
        icon: "bookmark",
      });
    }

    if ($branding.hasFeature(FEATURES.JOB_MARKET)) {
      customerGroup.push({
        id: "myTenders",
        to: localePath({ name: "myTenders" }),
        text: i18n.t("navbar.user.myTenders"),
        icon: "couch",
      });
    }

    // Group: Logout
    const logoutGroup = [
      {
        id: "logout",
        click: async () => {
          // Only leave the current page when it's guarded by an auth
          // middleware (`authenticated`/`admin`); on public pages the user
          // should stay where they are after logging out. The `admin` guard
          // lives on the layout, so pass `$nuxt.layout` alongside the route.
          const guardedRoute = isAuthGuardedRoute(
            router.currentRoute,
            instance?.$nuxt?.layout
          );
          if (guardedRoute) {
            await router.push(localePath({ name: "indexPage" }));
          }
          logout();
        },
        text: i18n.t("navbar.user.logout"),
        icon: "sign-out-alt",
      },
    ];

    const items = mergeGroupsWithDividers([
      adminGroup,
      crmGroup,
      jobMarketGroup,
      settingsGroup,
      customerGroup,
      logoutGroup,
    ]);

    return [
      {
        id: "user-menu",
        text: userStore.me.name,
        image: $helpers.getProfilePictureThumbnailUrl(
          userStore.me.profilePhotoId
        ),
        isLast: true,
        items,
      },
    ];
  });

  return {
    userMenu,
  };
}
